import React, { useEffect, useRef, useState } from "react"
import * as styles from "./hubspot-form.module.css"

// Global state to track script loading
let hubspotScriptLoading = false
let hubspotScriptLoaded = false
const pendingForms = []
let hubspotAutoInstanceCount = 0

const getNextAutoInstanceId = () => {
  hubspotAutoInstanceCount += 1
  return `auto-${hubspotAutoInstanceCount}`
}

const HubSpotForm = ({
  formId,
  portalId = "146447711",
  region = "eu1",
  className = "",
  idModifier,
}) => {
  const [autoInstanceId] = useState(() => getNextAutoInstanceId())
  const resolvedIdModifier = idModifier ?? autoInstanceId
  const formInstanceId = `${formId}-${resolvedIdModifier}`
  const mountRef = useRef(null)

  useEffect(() => {
    const mountNode = mountRef.current
    if (!mountNode) {
      return undefined
    }

    const embedTarget = document.createElement("div")
    embedTarget.id = `hubspot-form-container-${formId}-${resolvedIdModifier}`
    mountNode.innerHTML = ""
    mountNode.appendChild(embedTarget)

    const createForm = () => {
      if (window.hbspt) {
        if (!mountRef.current || !mountRef.current.contains(embedTarget)) {
          return
        }

        embedTarget.innerHTML = ""

        window.hbspt.forms.create({
          region: region,
          portalId: portalId,
          formId: formId,
          target: `#${embedTarget.id}`,
          // Prevent shared state collisions when the same HubSpot form is rendered multiple times on one page.
          formInstanceId,
        })
      }
    }

    const scriptSrc = "https://js-eu1.hsforms.net/forms/embed/v2.js"

    // If script is already loaded, create form immediately
    if (hubspotScriptLoaded && window.hbspt) {
      createForm()
      return
    }

    let checkHubSpot = null

    const cleanup = () => {
      if (checkHubSpot) {
        clearInterval(checkHubSpot)
        checkHubSpot = null
      }

      const index = pendingForms.indexOf(createForm)
      if (index > -1) {
        pendingForms.splice(index, 1)
      }

      if (mountRef.current && mountRef.current.contains(embedTarget)) {
        mountRef.current.removeChild(embedTarget)
      }
    }

    // Add this form to the pending queue
    pendingForms.push(createForm)

    // If script is already loading, still return cleanup so this callback
    // is removed if the component unmounts before script load finishes.
    if (hubspotScriptLoading) {
      return cleanup
    }

    // Check if script element exists
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`)

    if (!existingScript) {
      hubspotScriptLoading = true
      const script = document.createElement("script")
      script.src = scriptSrc
      script.defer = true
      document.head.appendChild(script)
      script.onload = () => {
        hubspotScriptLoaded = true
        hubspotScriptLoading = false
        while (pendingForms.length > 0) {
          const formCreator = pendingForms.shift()
          formCreator()
        }
      }
    } else {
      hubspotScriptLoading = true
      checkHubSpot = setInterval(() => {
        if (window.hbspt) {
          clearInterval(checkHubSpot)
          checkHubSpot = null
          hubspotScriptLoaded = true
          hubspotScriptLoading = false
          while (pendingForms.length > 0) {
            const formCreator = pendingForms.shift()
            formCreator()
          }
        }
      }, 100)
    }

    return cleanup
  }, [formId, formInstanceId, portalId, region, resolvedIdModifier])

  if (!formId) {
    return null
  }

  return (
    <div className={`${styles.hubspotForm} ${className} form-embed`}>
      <div ref={mountRef} />
    </div>
  )
}

export default HubSpotForm
